import React, { useRef, useState } from 'react';
import { LayoutOrientations } from '../../globals';
import { Chevron } from '../Iconset';
import Spacer from '../layout/Spacer';
import { MenuClosureReasons, MenuItemHoverTime } from '../../interfaces/Menus';
import AnalyticsManager from '../../classes/AnalyticsManager';
import { MenuAlignments } from '../../interfaces/enums';

interface ChildProps
{
	key?: React.Key;
	onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	disabled?: boolean;
}

interface IMenuProps
{
	children?: React.ReactNode;
	id?: number;
	label?: React.ReactNode;
	keepOpen?: boolean;
	hideChevron?: boolean;
	tooltip?: string;
	hoverMenu?: boolean;
	orientation?: LayoutOrientations;
	shouldBeOpen?: boolean;
	className?: string;
	iconRenderer?: JSX.Element;
	disabled?: boolean;
	overrideBackgroundColor?: string;
	menuOpenCallback?: (menuId: number) => void;
	menuClosedCallback?: (reason: MenuClosureReasons) => void;
	align?: MenuAlignments;
}

export default function Menu(props: IMenuProps)
{
	const [isOpen, setIsOpen] = useState(false);
	const [openedMenu, setOpenedMenu] = useState(-1);
	const wasOpen = useRef<boolean>(false);
	
	const rootRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const hoverOpenTimer = useRef<any>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const itemHoverTimer = useRef<any>(null);
	const [hoveredChild, setHoveredChild] = useState(-1);

	const orientation = props.orientation !== undefined ? props.orientation : LayoutOrientations.Vertical;
	const isActuallyOpened = props.shouldBeOpen || isOpen;
	const alignment = props.align || MenuAlignments.Left;

	// menu opened anew
	if (isActuallyOpened && !wasOpen.current)
	{
		wasOpen.current = true;
	}

	// menu was closed
	if (!isActuallyOpened && wasOpen.current)
	{
		wasOpen.current = false;
		setOpenedMenu(-1);
		setHoveredChild(-1);
	}

	function menuWantsToOpen(menuId: number)
	{
		setOpenedMenu(menuId);
	}

	function open()
	{
		if (props.menuOpenCallback)
		{
			props.menuOpenCallback(props.id ?? 0);
		}
		else
		{
			setIsOpen(true);
		}
	}

	function close(reason: MenuClosureReasons)
	{
		if (props.keepOpen)
		{
			return;
		}

		if (props.menuClosedCallback)
		{
			props.menuClosedCallback(reason);
		}

		setIsOpen(false);
		setHoveredChild(-1);
		setOpenedMenu(-1);
	}

	function onSubMenuClosed(reason: MenuClosureReasons)
	{
		//console.log("submenu closed: " + reason);

		switch (reason)
		{
			case MenuClosureReasons.Cancelled:
			case MenuClosureReasons.ClickOut:
			case MenuClosureReasons.Executed:
			default:
				close(reason);
				break;
		}
	}

	function renderMenuItems()
	{
		const rootRect = rootRef.current?.parentElement?.getBoundingClientRect();
		if(!rootRect) return null;

		const style: React.CSSProperties = {};
		if (orientation === LayoutOrientations.Horizontal)
		{
			style.left = rootRect.right;
			style.top = rootRect.top;
		}
		else if (orientation === LayoutOrientations.Vertical)
		{
			style.left = rootRect.left;
			style.top = rootRect.bottom;
		}

		if(alignment === MenuAlignments.Right)
		{
			delete style.left;
			style.right = window.innerWidth - rootRect.right;
		}

		return (
			<div className="menuContent"
				data-name={"menu-content"}
				ref={contentRef}
				style={style}
			>
				{
					React.Children.map(props.children, (child: React.ReactNode, idx) =>
					{
						if (React.isValidElement<ChildProps>(child))
						{
							return (
								<div className={"menuItemContainer"}
									data-enabled={child.props.disabled ? "false" : "true"}
									onMouseEnter={() =>
									{
										itemHoverTimer.current = setTimeout(() =>
										{
											setHoveredChild(idx);
										}, MenuItemHoverTime);
									}}
									onMouseLeave={() =>
									{
										clearTimeout(itemHoverTimer.current);
									}}
									key={idx}
									onClick={(e) =>
									{
										if (child.props.onClick)
										{
											if (child.props['data-name'])
											{
												AnalyticsManager.managedClick(child.props['data-name'], "Menu");
											}
											child.props.onClick(e);
											close(MenuClosureReasons.Executed);
										}
									}}
								>
									{
										renderMenuItem(child, idx)
									}
								</div>
							);
						}
						else
						{
							<React.Fragment key={idx}>
								{child}
							</React.Fragment>
						}
					})
				}
			</div>
		);
	}

	function renderMenuItem(item: React.ReactNode, idx: number)
	{
		if (!item || !React.isValidElement(item))
			return item;

		if (item.type === Menu)
		{
			return React.cloneElement<any>(item, {
				id: idx,
				menuClosedCallback: onSubMenuClosed,
				orientation: LayoutOrientations.Horizontal,
				hoverMenu: true,
				menuOpenCallback: menuWantsToOpen,
				shouldBeOpen: idx === openedMenu && hoveredChild === idx,
			});
		}

		if (item.props.onClick)
		{
			return React.cloneElement<any>(item, {
				onClick: function (e: React.MouseEvent)
				{
					e.stopPropagation();
					if (item.props['data-name'])
					{
						AnalyticsManager.managedClick(item.props['data-name'], "Menu");
					}
					item.props.onClick();
					close(MenuClosureReasons.Executed);
				},
			});
		}

		return item;
	}

	return (
		<div data-name={props["data-name"] ?? ("menu-container-" + props.label)}
			className={[
				"menuContainer",
				(props.disabled ? "dimmedLow" : ""),
				(props.className ? props.className : ""),
			].join(' ')}
			data-status={props.disabled ? "disabled" : ""}
			ref={rootRef}
			onClick={() =>
			{
				if(React.Children.count(props.children) > 0)
				{
					open();
				}
			}}
			onMouseEnter={(e) =>
			{
				if (props.hoverMenu)
				{
					hoverOpenTimer.current = setTimeout(() =>
					{
						open();
					}, MenuItemHoverTime);
				}
			}}
			onMouseLeave={() =>
			{
				if (hoverOpenTimer.current)
				{
					clearTimeout(hoverOpenTimer.current);
				}
			}}
			data-tooltip={props.tooltip}
		>
			<div className={[
				"menuHeader",
				(props.className ? props.className : ""),
			].join(' ')}
			data-status={isActuallyOpened ? "opened" : "closed"}
			data-enabled={props.disabled ? "false" : "true"}
			ref={headerRef}
			>
				{ props.iconRenderer }
				{
					!props.label ? null :
						<div className={[
							"menuLabel",
							(props.className ? props.className : ""),
						].join(' ')}
						style={{
							...(props.overrideBackgroundColor && { backgroundColor: props.overrideBackgroundColor }) 
						}}
						>
							{props.label}
						</div>
				}
				{
					props.hideChevron || props.disabled ? null :
						<>
							<Spacer size={{ x: props.className?.includes("menuItemSmall") ? "0px" : "5px" }} />
							<Spacer stretchTo={{ x: "100%" }} />
							<Chevron scale={0.7} rotate={orientation === LayoutOrientations.Vertical ? 0 : -90} />
						</>
				}
			</div>
			{
				!isActuallyOpened || props.disabled ? null : renderMenuItems()
			}
		</div>
	);
}